/**
 * Debug logger for browser detection utilities.
 * Enable by setting localStorage.setItem('browserDetection:debug', 'true')
 * or by setting the environment variable VITE_BROWSER_DETECTION_DEBUG=true
 */

import type { OperatingSystem, DeviceType, BrowserName } from "./types";
import {
  getFriendlyOSName,
  getFriendlyDeviceName,
  getFriendlyBrowserName,
  getSupportDescription,
} from "./userFriendlyNames";
import { DEBUG_KEY } from "./constants";

/**
 * Checks if debug logging is enabled.
 */
function isDebugEnabled(): boolean {
  // Check localStorage first (for runtime toggling)
  if (typeof window !== "undefined" && window.localStorage) {
    const stored = window.localStorage.getItem(DEBUG_KEY);
    if (stored === "true") return true;
    if (stored === "false") return false;
  }

  // Check environment variable (for build-time configuration)
  if (typeof import.meta !== "undefined" && import.meta.env) {
    return import.meta.env.VITE_BROWSER_DETECTION_DEBUG === "true";
  }

  return false;
}

/**
 * Debug logger for browser detection.
 */
export const debugLogger = {
  /**
   * Logs a debug message if debug mode is enabled.
   */
  log: (message: string, ...args: unknown[]): void => {
    if (!isDebugEnabled()) return;

    const prefix = "[BrowserDetection]";
    if (args.length > 0) {
      // Format objects as readable strings
      const formattedArgs = args.map((arg) => {
        if (typeof arg === "object" && arg !== null) {
          try {
            return JSON.stringify(arg, null, 2);
          } catch {
            return String(arg);
          }
        }
        return arg;
      });
      console.log(prefix, message, ...formattedArgs);
    } else {
      console.log(prefix, message);
    }
  },

  /**
   * Logs detection results in a structured format.
   */
  logDetection: (
    category: "OS" | "Device" | "Browser",
    detected: string,
    details?: Record<string, unknown>
  ): void => {
    if (!isDebugEnabled()) return;

    const prefix = `[BrowserDetection:${category}]`;

    // Get user-friendly name
    let friendlyName: string;
    if (category === "OS") {
      friendlyName = getFriendlyOSName(detected as OperatingSystem);
    } else if (category === "Device") {
      friendlyName = getFriendlyDeviceName(detected as DeviceType);
    } else {
      friendlyName = getFriendlyBrowserName(detected as BrowserName);
    }

    console.log(`${prefix} Detected: ${detected} (${friendlyName})`);

    if (details && Object.keys(details).length > 0) {
      // Use console.table for structured data (shows as a nice table)
      console.table(details);
    }
  },

  /**
   * Logs the full user agent for debugging.
   */
  logUserAgent: (): void => {
    if (!isDebugEnabled()) return;
    console.log("[BrowserDetection:UserAgent]", navigator.userAgent);
  },

  /**
   * Logs all detection results in a summary format with user-friendly descriptions.
   */
  logSummary: (results: {
    os: string;
    device: string;
    browser: string;
    browserVersion?: string | null;
  }): void => {
    if (!isDebugEnabled()) return;

    const friendlyOS = getFriendlyOSName(results.os as OperatingSystem);
    const friendlyDevice = getFriendlyDeviceName(results.device as DeviceType);
    const friendlyBrowser = getFriendlyBrowserName(
      results.browser as BrowserName
    );
    const supportDescription = getSupportDescription(
      results.os as OperatingSystem,
      results.device as DeviceType,
      results.browser as BrowserName,
      results.browserVersion || null
    );

    console.group("🔍 [BrowserDetection:Summary] - Support Team Info");
    console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
    console.log("📱 User-Friendly Description:");
    console.log(`   ${supportDescription}`);
    console.log("");
    console.log("🔧 Technical Details:");
    console.log(`   Operating System: ${results.os} (${friendlyOS})`);
    console.log(`   Device Type: ${results.device} (${friendlyDevice})`);
    console.log(`   Browser: ${results.browser} (${friendlyBrowser})`);
    if (results.browserVersion) {
      console.log(`   Browser Version: ${results.browserVersion}`);
    }
    console.log("");
    console.log("🌐 User Agent String:");
    console.log(`   ${navigator.userAgent}`);
    console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
    console.groupEnd();
  },
};

/**
 * Enables debug logging (stores in localStorage).
 */
export function enableDebugLogging(): void {
  if (typeof window !== "undefined" && window.localStorage) {
    window.localStorage.setItem(DEBUG_KEY, "true");
    console.log("[BrowserDetection] Debug logging enabled");
  }
}

/**
 * Disables debug logging (removes from localStorage).
 */
export function disableDebugLogging(): void {
  if (typeof window !== "undefined" && window.localStorage) {
    window.localStorage.setItem(DEBUG_KEY, "false");
    console.log("[BrowserDetection] Debug logging disabled");
  }
}
